import { Component, OnInit, OnDestroy, ViewChildren, QueryList, ElementRef, ViewContainerRef, ComponentRef, AfterViewInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store, select } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { FileBrowserModalComponent } from 'src/app/common/bhub-common-module/file-browser-modal/file-browser-modal.component';
import { AppState } from 'src/app/store/app-state.reducer';
import { selectSelectedApplication, selectCurrentUserEmail, selectCurrentUserProfile } from 'src/app/store/app-state.selectors';
import { CustomViewService } from 'src/app/services/custom-view.service';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { PdfGenerationService } from 'src/app/services/pdf-generation.service';
import { DynamicTableFilters } from 'src/app/services/models/dynamic-table.models';
import { CustomViewListItem } from 'src/app/services/models/custom-view.models';
import { ViewDefinition, ViewManager, ViewTab, ViewCell, CellType, ViewRepeatingSection, CellPosition } from '../admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { CellFactoryService } from '../admin/admin-components/custom-views-config/view-builder/services/cell-factory.service';
import { CellFilterService } from 'src/app/common/bhub-common-module/services/cell-filter.service';
import { BaseCellComponent } from 'src/app/common/bhub-common-module/components/view-cells/base-cell.component';
import { isListContext } from 'src/app/common/bhub-common-module/utils/context-array.util';

export interface CustomView {
  id: number;
  name: string;
  description: string;
  createdBy: string;
  lastModified: Date;
  isShared: boolean;
  viewDefinition?: ViewDefinition;
}

interface CellInstance {
  id: string;
  viewCell: ViewCell;
  componentRef?: ComponentRef<BaseCellComponent>;
  // Set only for cells rendered inside a repeating section, so PDF edit-mode
  // drag/resize can be recorded as a per-row override rather than mutating the
  // shared template cell (which would move every row at once).
  sectionId?: string;
  rowIndex?: number;
  templateCellId?: string;
}

interface SectionRowInstance {
  rowIndex: number;
  rowData: any;
  cellInstances: CellInstance[];
}

interface SectionInstance {
  id: string;
  section: ViewRepeatingSection;
  rows: SectionRowInstance[];
  rowHeight: number; // section.height minus the 25px builder header
  totalHeight: number; // rowHeight * rows.length
}

@Component({
  selector: 'bhub-custom-views',
  standalone: false,
  templateUrl: './custom-views.component.html',
  styleUrl: './custom-views.component.scss'
})
export class CustomViewsComponent implements OnInit, OnDestroy, AfterViewInit {
  selectedView: CustomView | null = null;
  myViews: CustomView[] = [];
  sharedViews: CustomView[] = [];
  loading = false;
  loadingViews = false;
  error: string | null = null;

  // View rendering properties
  currentViewDefinition: ViewDefinition | null = null;
  viewManager: ViewManager | null = null;
  currentTab: ViewTab | null = null;
  cellInstances: CellInstance[] = [];
  sectionInstances: SectionInstance[] = [];

  // PDF generation state
  generatingPdf = false;

  // PDF edit mode - allows ephemeral edits to cells before generating PDF
  pdfEditMode = false;
  pdfEditingCellId: string | null = null;
  pdfOverrides: Map<string, string> = new Map(); // cell instance ID → override text
  // Per-row position/size edits for repeating-section cells (ephemeral, like pdfOverrides).
  // Keyed by cell instance ID (`${cellId}_row${rowIndex}`).
  pdfPositionOverrides: Map<string, CellPosition> = new Map();
  // Cells deleted in PDF edit mode (ephemeral). Keyed by cell instance ID, so a
  // single repeating-section row can be removed without affecting other rows.
  pdfDeletedCellIds: Set<string> = new Set();
  private pdfEditingInitialText: string = '';
  pdfDraggingCellId: string | null = null;
  private pdfDragStartX = 0;
  private pdfDragStartY = 0;
  private pdfDragOrigX = 0;
  private pdfDragOrigY = 0;
  private pdfDragCell: CellInstance | null = null;
  private pdfDragMoved = false;
  private boundOnPdfDragMove: ((e: MouseEvent) => void) | null = null;
  private boundOnPdfDragEnd: ((e: MouseEvent) => void) | null = null;

  // PDF cell resize state
  pdfResizingCellId: string | null = null;
  private pdfResizeStartX = 0;
  private pdfResizeStartY = 0;
  private pdfResizeOrigW = 0;
  private pdfResizeOrigH = 0;
  private pdfResizeCell: CellInstance | null = null;
  private boundOnPdfResizeMove: ((e: MouseEvent) => void) | null = null;
  private boundOnPdfResizeEnd: ((e: MouseEvent) => void) | null = null;

  // Tracks the last resolved filter values per context to avoid redundant re-fetches
  private lastContextFilters: { [contextKey: string]: string } = {};
  // Tracks the last resolved filter signature per section to avoid redundant dataset re-fetches
  private lastSectionFilterSignatures: { [sectionId: string]: string } = {};
  // Guard to prevent re-entrant section dataset fetches
  private sectionFetchesInFlight: Set<string> = new Set();
  // Guard to prevent re-entrant rendering
  private isRenderingSectionCells = false;

  @ViewChildren('cellContainer', { read: ViewContainerRef }) cellContainers!: QueryList<ViewContainerRef>;
  @ViewChildren('sectionCellContainer', { read: ViewContainerRef }) sectionCellContainers!: QueryList<ViewContainerRef>;

  private selectedAppId: number | null = null;
  private currentUserEmail: string = '';
  private currentUserContext: any = null;
  private destroy$ = new Subject<void>();

  constructor(
    private customViewService: CustomViewService,
    private dynamicTableService: DynamicTableService,
    private pdfGenerationService: PdfGenerationService,
    private store: Store<AppState>,
    private cellFactory: CellFactoryService,
    private cellFilterService: CellFilterService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    // Subscribe to selected application
    this.store.pipe(
      select(selectSelectedApplication),
      takeUntil(this.destroy$)
    ).subscribe(appId => {
      this.selectedAppId = appId;
      if (appId) {
        this.loadViews();
      }
    });

    // Subscribe to current user email
    this.store.pipe(
      select(selectCurrentUserEmail),
      takeUntil(this.destroy$)
    ).subscribe(email => {
      this.currentUserEmail = email || 'Unknown';
    });

    // Subscribe to full user profile and keep $user in the active viewManager
    this.store.pipe(
      select(selectCurrentUserProfile),
      takeUntil(this.destroy$)
    ).subscribe(profile => {
      this.currentUserContext = profile;
      if (this.viewManager) {
        this.viewManager.contextData = this.viewManager.contextData || {};
        this.viewManager.contextData['$user'] = profile;
      }
    });
  }

  ngAfterViewInit(): void {
    // Render cells after view init
    this.cellContainers.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.renderCells();
    });

    // Render section cells when their containers appear
    this.sectionCellContainers.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.renderSectionCells();
    });
  }

  ngOnDestroy(): void {
    this.cleanupCells();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadViews(): void {
    if (!this.selectedAppId) {
      this.error = 'No application selected';
      return;
    }

    this.loadingViews = true;
    this.error = null;

    // Load active views for the current user (includes personal and shared views)
    this.customViewService.getActiveViewsForUser(this.selectedAppId, this.currentUserEmail)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (views: CustomViewListItem[]) => {
          // Separate views into personal and shared
          const allViews = views.map(view => ({
            id: view.viewId,
            name: view.viewName,
            description: view.viewDescription || '',
            createdBy: view.createdBy,
            lastModified: view.modifiedDate,
            isShared: view.isShared,
            displayOrder: view.displayOrder,
            viewDefinition: undefined // Will be loaded when view is selected
          }));

          // Split into my views (created by current user) and shared views, sorted by displayOrder
          const sortByOrder = (a: any, b: any) => (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999);
          this.myViews = allViews.filter(view => view.createdBy === this.currentUserEmail).sort(sortByOrder);
          this.sharedViews = allViews.filter(view => view.createdBy !== this.currentUserEmail && view.isShared).sort(sortByOrder);

          this.loadingViews = false;
        },
        error: (error) => {
          console.error('Error loading custom views:', error);
          this.error = 'Failed to load custom views. Please try again.';
          this.loadingViews = false;
        }
      });
  }

  onViewChange(event: any): void {
    if (this.selectedView) {
      this.loadViewData();
    } else {
      this.cleanupCurrentView();
    }
  }

  loadViewData(): void {
    if (!this.selectedView) {
      this.cleanupCurrentView();
      return;
    }

    this.loading = true;

    // Load the full view definition from the backend
    this.customViewService.getCustomView(this.selectedView.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (viewDefinition) => {
          this.loading = false;

          // Store the view definition for rendering
          this.currentViewDefinition = viewDefinition;
          this.selectedView!.viewDefinition = viewDefinition;

          // Create a view manager for rendering context
          this.viewManager = {
            contextData: viewDefinition.viewManager?.contextData || {},
            contextInstructions: viewDefinition.viewManager?.contextInstructions || {},
            globalFilters: viewDefinition.viewManager?.globalFilters || {},
          };
          this.viewManager.contextData['$user'] = this.currentUserContext;
          this.lastContextFilters = {};

          // Strip out any contextInstructions / orphan context data left behind
          // by additionalContexts that were since removed from cell configs.
          // Stale instructions can re-fetch huge datasets that aren't wanted anymore.
          this.pruneOrphanContexts();

          // Initialize any empty contexts from instructions
          this.initializeContextsFromInstructions();

          // Pre-initialize context variables from repeating sections
          this.initializeSectionContextVariables();

          // Set up the current tab (always start with first tab in view mode)
          this.setupCurrentTab();
        },
        error: (error) => {
          this.loading = false;
          this.error = 'Failed to load view data. Please try again.';
          console.error('Error loading view data:', error);
        }
      });
  }

  private setupCurrentTab(): void {
    if (!this.currentViewDefinition) return;

    // Sort tabs by order and always start with first tab in view mode
    const sortedTabs = [...this.currentViewDefinition.tabs].sort((a, b) => a.order - b.order);
    this.currentTab = sortedTabs.length > 0 ? sortedTabs[0] : null;

    if (this.currentTab) {
      this.setupCellInstances();
      // Render cells after a short delay to ensure DOM is ready
      setTimeout(() => this.renderCells(), 100);
    }
  }

  private setupCellInstances(): void {
    this.cleanupCells();
    this.lastSectionFilterSignatures = {};
    this.sectionFetchesInFlight.clear();

    if (!this.currentTab) return;

    this.cellInstances = this.currentTab.cells
      .filter(cell => !this.pdfDeletedCellIds.has(cell.id))
      .map(cell => ({
        id: cell.id,
        viewCell: cell
      }));

    // Set up section instances — one row per data item
    this.sectionInstances = (this.currentTab.repeatingSections || []).map(section => {
      const dataRows = this.getSectionDataRows(section);
      this.storeSectionDataToContext(section, dataRows);
      return this.buildSectionInstance(section, dataRows);
    });

    // Kick off async fetch for dataset-sourced sections
    (this.currentTab.repeatingSections || []).forEach(section => {
      if (section.dataSource === 'dataset' && section.datasetId) {
        this.fetchSectionDatasetRows(section);
      }
    });
  }

  /**
   * Build a cell instance for one row of a repeating section.
   * The template cell is shallow-cloned with its OWN position object so PDF
   * edit-mode drag/resize on one row doesn't move every row. Any previously
   * recorded per-row position override is re-applied so edits survive rebuilds.
   */
  private buildSectionCellInstance(
    templateCell: ViewCell,
    sectionId: string,
    rowIndex: number
  ): CellInstance {
    const instanceId = `${templateCell.id}_row${rowIndex}`;
    const override = this.pdfPositionOverrides.get(instanceId);
    const position: CellPosition = override
      ? { ...templateCell.position, ...override }
      : { ...templateCell.position };
    return {
      id: instanceId,
      viewCell: { ...templateCell, position },
      sectionId,
      rowIndex,
      templateCellId: templateCell.id
    };
  }

  private buildSectionInstance(section: ViewRepeatingSection, dataRows: any[]): SectionInstance {
    const rows: SectionRowInstance[] = dataRows.map((rowData, rowIndex) => ({
      rowIndex,
      rowData,
      cellInstances: section.cells
        .filter(cell => !this.pdfDeletedCellIds.has(`${cell.id}_row${rowIndex}`))
        .map(cell => this.buildSectionCellInstance(cell, section.id, rowIndex))
    }));
    // If no data, show at least one empty row so the template is visible
    if (rows.length === 0) {
      rows.push({
        rowIndex: 0,
        rowData: null,
        cellInstances: section.cells
          .filter(cell => !this.pdfDeletedCellIds.has(`${cell.id}_row0`))
          .map(cell => this.buildSectionCellInstance(cell, section.id, 0))
      });
    }
    // section.height from the builder includes a 25px header bar that's only
    // visible in the admin editor. The viewer uses just the body height.
    const rowHeight = section.height - 25;
    return {
      id: section.id,
      section,
      rows,
      rowHeight,
      totalHeight: rowHeight * Math.max(rows.length, 1)
    };
  }

  /**
   * Fetch dataset rows for a dataset-sourced repeating section, then rebuild its rows.
   * Includes dedup logic to avoid redundant fetches and guards against re-entrant calls.
   */
  private fetchSectionDatasetRows(section: ViewRepeatingSection): void {
    // Guard: skip if a fetch is already in flight for this section
    if (this.sectionFetchesInFlight.has(section.id)) {
      return;
    }

    const filters = this.cellFilterService.buildCellFilters(
      section.cellFilters,
      this.viewManager
    );

    // Dedup: skip if the filter signature hasn't changed since last fetch
    const filterSignature = JSON.stringify({ datasetId: section.datasetId, filters });
    if (this.lastSectionFilterSignatures[section.id] === filterSignature) {
      return;
    }

    this.sectionFetchesInFlight.add(section.id);

    this.dynamicTableService.selectEntries(
      section.datasetId!,
      -1,
      undefined,
      filters
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.sectionFetchesInFlight.delete(section.id);
        this.lastSectionFilterSignatures[section.id] = filterSignature;

        const dataRows = response.rowData || [];
        // Store query results to context variable if configured
        this.storeSectionDataToContext(section, dataRows);
        // Find and replace the section instance
        const idx = this.sectionInstances.findIndex(si => si.id === section.id);
        if (idx !== -1) {
          // Destroy existing component refs for this section
          this.sectionInstances[idx].rows.forEach(row => {
            row.cellInstances.forEach(ci => {
              if (ci.componentRef) {
                ci.componentRef.destroy();
              }
            });
          });
          this.sectionInstances[idx] = this.buildSectionInstance(section, dataRows);
          // Re-render after DOM updates
          setTimeout(() => this.renderSectionCells(), 50);
        }
      },
      error: (error) => {
        this.sectionFetchesInFlight.delete(section.id);
        console.error(`[CustomViews] Error fetching section dataset rows for section ${section.id}:`, error);
      }
    });
  }

  private renderCells(): void {
    if (!this.cellContainers || this.cellContainers.length === 0) {
      // Still try to render section cells even if no main cells
      this.renderSectionCells();
      return;
    }

    const containers = this.cellContainers.toArray();

    this.cellInstances.forEach((cellInstance, index) => {
      if (index >= containers.length) return;
      this.renderCellInstance(cellInstance, containers[index]);
    });

    // Also render section cells
    this.renderSectionCells();
  }

  private renderSectionCells(): void {
    if (this.isRenderingSectionCells) return; // Prevent re-entrant calls
    if (!this.sectionCellContainers || this.sectionCellContainers.length === 0) return;

    this.isRenderingSectionCells = true;

    const sectionContainers = this.sectionCellContainers.toArray();

    this.sectionInstances.forEach(sectionInstance => {
      sectionInstance.rows.forEach(row => {
        // Each row gets its own viewManager with that row's data as $sectionItem
        const rowViewManager = this.buildSectionViewManager(sectionInstance.section, row.rowData);

        row.cellInstances.forEach(cellInstance => {
          // Skip if already rendered with a component
          if (cellInstance.componentRef) return;

          const container = sectionContainers.find(c => {
            const el = c.element.nativeElement;
            return el.getAttribute('data-cell-id') === cellInstance.id;
          });
          if (container) {
            this.renderCellInstance(cellInstance, container, rowViewManager);
          }
        });
      });
    });

    this.isRenderingSectionCells = false;
  }

  /**
   * Get the data rows for a section from its data source (synchronous sources only).
   * Dataset sources are fetched asynchronously via fetchSectionDatasetRows().
   */
  private getSectionDataRows(section: ViewRepeatingSection): any[] {
    if (section.dataSource === 'context' && section.contextVariable) {
      const contextArray = this.viewManager?.contextData?.[section.contextVariable];
      if (Array.isArray(contextArray)) {
        return contextArray;
      }
    }
    return [];
  }

  private storeSectionDataToContext(section: ViewRepeatingSection, dataRows: any[]): void {
    if (!section.storeContextVariable || !this.viewManager?.contextData) return;
    this.onContextDataChange({ [section.storeContextVariable]: dataRows });
  }

  private initializeSectionContextVariables(): void {
    if (!this.viewManager || !this.currentViewDefinition) return;
    this.currentViewDefinition.tabs.forEach(tab => {
      (tab.repeatingSections || []).forEach(section => {
        if (section.storeContextVariable) {
          if (!this.viewManager!.contextData[section.storeContextVariable]) {
            this.viewManager!.contextData[section.storeContextVariable] = [];
          }
        }
      });
    });
  }

  /**
   * Build a viewManager for a specific section row, injecting the row's data as $sectionItem.
   */
  private buildSectionViewManager(section: ViewRepeatingSection, rowData?: any): ViewManager | null {
    if (!this.viewManager) return null;

    return {
      ...this.viewManager,
      contextData: {
        ...(this.viewManager.contextData || {}),
        $sectionItem: rowData ?? null
      }
    };
  }

  private renderCellInstance(cellInstance: CellInstance, container: ViewContainerRef, viewManagerOverride?: ViewManager | null): void {
    const componentType = this.cellFactory.getCellComponent(cellInstance.viewCell.type);

    if (!componentType) {
      console.warn('No component found for cell type:', cellInstance.viewCell.type);
      return;
    }

    // Clear existing component
    container.clear();

    // Create new component
    cellInstance.componentRef = container.createComponent(componentType);
    const component = cellInstance.componentRef.instance;

    // Set component inputs
    component.cell = cellInstance.viewCell;
    component.isEditMode = false; // Always view mode

    // Pass PDF edit mode to cells on PDF tabs
    if (component.hasOwnProperty('pdfEditMode')) {
      (component as any).pdfEditMode = this.pdfEditMode && this.isPdfTab();
    }

    // Set additional inputs for specific cell types
    if (component.hasOwnProperty('viewManager')) {
      (component as any).viewManager = viewManagerOverride || this.viewManager;
    }

    if (component.hasOwnProperty('availableTabs')) {
      (component as any).availableTabs = this.currentViewDefinition?.tabs || [];
    }

    // Listen for context changes
    if (component.hasOwnProperty('contextDataChange')) {
      const contextEmitter = (component as any).contextDataChange;
      if (contextEmitter && contextEmitter.subscribe) {
        contextEmitter.pipe(takeUntil(this.destroy$)).subscribe((contextMap: {[contextKey: string]: any}) => {
          this.onContextDataChange(contextMap);
        });
      }
    }

    // Listen for cell refresh requests (from action cells after save)
    if (component.hasOwnProperty('refreshCells')) {
      const refreshEmitter = (component as any).refreshCells;
      if (refreshEmitter && refreshEmitter.subscribe) {
        refreshEmitter.pipe(takeUntil(this.destroy$)).subscribe((cellIds: string[]) => {
          this.onRefreshCells(cellIds);
        });
      }
    }

    // Listen for tab navigation (for filter list cells)
    if (component.hasOwnProperty('tabNavigation')) {
      const navEmitter = (component as any).tabNavigation;
      if (navEmitter && navEmitter.subscribe) {
        navEmitter.pipe(takeUntil(this.destroy$)).subscribe((tabId: string) => {
          this.navigateToTab(tabId);
        });
      }
    }

    // Trigger change detection
    cellInstance.componentRef.changeDetectorRef.detectChanges();
  }

  private initializeContextsFromInstructions(): void {
    if (!this.viewManager) return;

    // Ensure all contexts mentioned in instructions are initialized.
    // Match the shape to the instruction's intent (limit !== 1 ⇒ list-shape
    // wrapper) so a context that's supposed to be a list starts as one even
    // before any fetch has populated it.
    if (this.viewManager.contextInstructions) {
      Object.keys(this.viewManager.contextInstructions).forEach(contextKey => {
        if (!this.viewManager!.contextData.hasOwnProperty(contextKey)) {
          const instruction = this.viewManager!.contextInstructions[contextKey];
          const datasetId = instruction?.instructions?.datasetId || null;
          const isListIntent = instruction?.instructions?.limit !== 1;
          if (isListIntent) {
            // List contexts are bare arrays — an empty one is simply [].
            this.viewManager!.contextData[contextKey] = [];
          } else {
            this.viewManager!.contextData[contextKey] = {
              __metadata: { datasetId, isEmptyContext: true, isNewRecord: false }
            };
          }
        }
      });
    }
  }

  // Drops orphan contextInstructions / contextData for context names no longer
  // produced by any cell. Instructions are strict (only additionalContexts produce
  // them); data is broader (any reference keeps the cached payload).
  private pruneOrphanContexts(): void {
    const vm = this.viewManager;
    if (!vm) return;
    if (!vm.contextInstructions && !vm.contextData) return;
    if (!this.currentViewDefinition?.tabs) return;

    const anyReferencedKeys = new Set<string>();
    const instructionProducerKeys = new Set<string>();

    for (const tab of this.currentViewDefinition.tabs) {
      for (const cell of tab.cells || []) {
        const cfg = cell.configuration;
        if (!cfg) continue;
        if (cfg.contextVariable) anyReferencedKeys.add(cfg.contextVariable);
        if (cfg.primaryContextVariable) anyReferencedKeys.add(cfg.primaryContextVariable);
        if (cfg.multiSelectContextVariable) anyReferencedKeys.add(cfg.multiSelectContextVariable);
        if (cfg.lookupContextVariable) anyReferencedKeys.add(cfg.lookupContextVariable);
        if (cfg.listDisplayContextVariable) anyReferencedKeys.add(cfg.listDisplayContextVariable);
        if (cfg.rowUpsertContextVariable) anyReferencedKeys.add(cfg.rowUpsertContextVariable);
        if (cfg.additionalContexts) {
          for (const ctx of cfg.additionalContexts) {
            if (ctx?.contextVariable) {
              anyReferencedKeys.add(ctx.contextVariable);
              instructionProducerKeys.add(ctx.contextVariable);
            }
          }
        }
        if (cfg.createEmptyContexts) {
          for (const ec of cfg.createEmptyContexts) {
            if (ec?.contextVariable) anyReferencedKeys.add(ec.contextVariable);
          }
        }
        if (cfg.lookupContextMappings) {
          for (const m of cfg.lookupContextMappings) {
            if (m?.contextVariable) anyReferencedKeys.add(m.contextVariable);
          }
        }
        if (cfg.inputContextMappings) {
          for (const m of cfg.inputContextMappings) {
            if (m?.contextVariable) anyReferencedKeys.add(m.contextVariable);
          }
        }
      }
      for (const section of tab.repeatingSections || []) {
        if (section?.storeContextVariable) anyReferencedKeys.add(section.storeContextVariable);
        if (section?.contextVariable) anyReferencedKeys.add(section.contextVariable);
      }
    }

    const isSystem = (k: string) => k.startsWith('$');
    const isCompanion = (k: string) => k.endsWith('__deleted');

    // Drop orphan contextInstructions — strict: must have a current producer.
    if (vm.contextInstructions) {
      for (const key of Object.keys(vm.contextInstructions)) {
        if (isSystem(key)) continue;
        if (!instructionProducerKeys.has(key)) {
          delete vm.contextInstructions[key];
        }
      }
    }

    // Drop orphan contextData — broader: any reference is enough to preserve
    // the cached data so upsert/lookup targets keep working across reloads.
    if (vm.contextData) {
      for (const key of Object.keys(vm.contextData)) {
        if (isSystem(key)) continue;
        if (isCompanion(key)) {
          const baseKey = key.slice(0, -'__deleted'.length);
          if (anyReferencedKeys.has(baseKey)) continue;
        }
        if (!anyReferencedKeys.has(key)) {
          delete vm.contextData[key];
        }
      }
    }

    // Wipe stale cached payload for keys whose instruction was dropped but
    // are still referenced by some other cell (e.g., upsert target).
    if (vm.contextData) {
      for (const key of Object.keys(vm.contextData)) {
        if (isSystem(key) || isCompanion(key)) continue;
        if (anyReferencedKeys.has(key) && !instructionProducerKeys.has(key)) {
          const existing = vm.contextData[key];
          if (isListContext(existing)) {
            vm.contextData[key] = [];
          } else {
            vm.contextData[key] = {
              __metadata: { isEmptyContext: true, isNewRecord: false }
            };
          }
        }
      }
    }
  }

  private onContextDataChange(contextMap: {[contextKey: string]: any}): void {
    if (this.viewManager) {
      if (!this.viewManager.contextData) {
        this.viewManager.contextData = {};
      }

      // Update all context objects from the map (never allow null — use empty object instead)
      Object.keys(contextMap).forEach(contextKey => {
        const value = contextMap[contextKey];
        if (value === null || value === undefined) {
          this.viewManager.contextData[contextKey] = {
            __metadata: { isEmptyContext: true, isNewRecord: false }
          };
        } else {
          this.viewManager.contextData[contextKey] = value;
        }
      });

      // Update all cell instances with new context data
      this.cellInstances.forEach(cellInstance => {
        if (cellInstance.componentRef) {
          const component = cellInstance.componentRef.instance;
          if (component.hasOwnProperty('viewManager')) {
            (component as any).viewManager = this.viewManager;
          }
        }
      });

      // Refresh cells that consume the updated contexts
      Object.keys(contextMap).forEach(contextKey => {
        this.refreshCellsUsingContext(contextKey);
      });

      // Check for and refresh dependent contexts
      this.refreshOrClearDependentContexts(Object.keys(contextMap));
    }
  }

  private refreshOrClearDependentContexts(updatedContextKeys: string[]): void {
    if (!this.viewManager) return;

    // Find all contexts that depend on the updated contexts
    const dependentContextsToRefresh: Set<string> = new Set();

    updatedContextKeys.forEach(updatedKey => {
      // Check all context instructions for dependencies on this key
      if (this.viewManager.contextInstructions) {
        Object.keys(this.viewManager.contextInstructions).forEach(contextKey => {
          const instruction = this.viewManager.contextInstructions[contextKey];

          // Check explicit dependency
          if (instruction?.depContextObj === updatedKey) {
            dependentContextsToRefresh.add(contextKey);
          }

          // Check filter-based dependencies
          if (instruction?.instructions?.filters) {
            const hasDependency = this.hasFilterDependency(instruction.instructions.filters, updatedKey);
            if (hasDependency) {
              dependentContextsToRefresh.add(contextKey);
            }
          }
        });
      }
    });

    // Don't re-fetch contexts that were just explicitly updated in this cycle —
    // they were populated by a cell (e.g., Lookup) and re-fetching would overwrite
    // the local data with a stale or empty backend result.
    updatedContextKeys.forEach(key => dependentContextsToRefresh.delete(key));

    // Only clear cached filter signatures for dependent contexts whose resolved
    // filter values actually changed (e.g., a new inventory ID was selected).
    // If a calc cell stored a computed field back on the same context object,
    // the filter values haven't changed and we skip the clear — preventing an
    // unnecessary re-fetch that would wipe local edits.
    dependentContextsToRefresh.forEach(contextKey => {
      const instruction = this.viewManager.contextInstructions?.[contextKey];
      if (instruction?.instructions?.filters) {
        const newSignature = JSON.stringify(this.resolveContextFilters(
          instruction.instructions.filters,
          { multiValue: instruction.instructions.limit !== 1 }
        ));
        if (this.lastContextFilters[contextKey] !== newSignature) {
          delete this.lastContextFilters[contextKey];
        }
      } else {
        // No filters — always allow re-fetch
        delete this.lastContextFilters[contextKey];
      }
    });

    // Refresh dependent contexts if their dependencies are resolved.
    dependentContextsToRefresh.forEach(contextKey => {
      if (this.areDependenciesResolved(contextKey)) {
        this.refreshContext(contextKey);
      }
    });
  }

  private areDependenciesResolved(contextKey: string): boolean {
    if (!this.viewManager?.contextInstructions?.[contextKey]) {
      return false;
    }

    const instruction = this.viewManager.contextInstructions[contextKey];

    // Get all dependencies (explicit and from filters)
    const dependencies = this.getAllContextDependencies(instruction);

    // If no dependencies, it can be resolved
    if (dependencies.length === 0) {
      return true;
    }

    // Check if all dependencies have real data
    return dependencies.every(depContextKey => {
      const depContextData = this.viewManager.contextData?.[depContextKey];

      if (!depContextData || depContextData === null) {
        return false;
      }

      // Check if dependency has actual data (not just empty structure)
      if (typeof depContextData === 'object') {
        // Skip empty contexts - they are intentionally empty for new record creation
        if (depContextData.__metadata?.isEmptyContext === true) {
          return false;
        }

        // Skip new record contexts - these have default values but aren't real data
        if (depContextData.__isNew === true) {
          return false;
        }

        // For objects, check if they have meaningful data beyond metadata and internal flags
        const hasRealData = Object.keys(depContextData).some(key => {
          // Skip internal properties
          if (key === '__metadata' || key === '__isNew') return false;
          const value = depContextData[key];
          return value !== null && value !== undefined && value !== '';
        });
        return hasRealData;
      }

      return true;
    });
  }

  private refreshContext(contextKey: string): void {
    if (!this.viewManager || !this.viewManager.contextInstructions || !this.viewManager.contextInstructions[contextKey]) {
      return;
    }

    const instruction = this.viewManager.contextInstructions[contextKey];

    // Resolve filters before making the request. A single-record instruction keeps
    // first-element semantics so its limit-1 fetch stays deterministic.
    const resolvedFilters = this.resolveContextFilters(
      instruction.instructions.filters,
      { multiValue: instruction.instructions.limit !== 1 }
    );

    // Skip re-fetch if resolved filter values haven't changed since last fetch
    const filterSignature = JSON.stringify(resolvedFilters);
    if (this.lastContextFilters[contextKey] === filterSignature) {
      return;
    }
    this.lastContextFilters[contextKey] = filterSignature;

    // A list-intent context (limit !== 1) always settles on an array, whether or
    // not the fetch returned rows. Otherwise downstream cells that rely on the list
    // shape (e.g. a data entry table bound in context-source mode, an upsert target)
    // lose their list-ness on the empty branch and start writing flat single-record
    // shapes from then on.
    const isListIntent = instruction.instructions.limit !== 1;
    const datasetId = instruction.instructions.datasetId;

    this.dynamicTableService.selectEntries(
      datasetId,
      instruction.instructions.limit,
      undefined,
      resolvedFilters
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        const rows = response.rowData || [];
        if (isListIntent) {
          this.viewManager!.contextData[contextKey] = rows;
        } else if (rows.length > 0) {
          this.viewManager!.contextData[contextKey] = rows[0];
        } else {
          this.viewManager!.contextData[contextKey] = {
            __metadata: { datasetId, isEmptyContext: true, isNewRecord: false }
          };
        }

        this.refreshCellsUsingContext(contextKey);
        this.triggerDependentContexts(contextKey);
      },
      error: (error) => {
        console.error(`[CustomViews] Error refreshing context '${contextKey}':`, error);
        if (isListIntent) {
          this.viewManager!.contextData[contextKey] = [];
        } else {
          this.viewManager!.contextData[contextKey] = {
            __metadata: { datasetId, isEmptyContext: true, isNewRecord: false }
          };
        }

        this.refreshCellsUsingContext(contextKey);
        this.triggerDependentContexts(contextKey);
      }
    });
  }

  private triggerDependentContexts(completedContextKey: string): void {
    if (!this.viewManager?.contextInstructions) return;

    // Find all contexts that depend on the completed context
    const dependentContexts: string[] = [];

    Object.keys(this.viewManager.contextInstructions).forEach(contextKey => {
      const instruction = this.viewManager.contextInstructions[contextKey];

      // Check explicit dependency
      if (instruction?.depContextObj === completedContextKey) {
        dependentContexts.push(contextKey);
      }

      // Check filter-based dependencies
      if (instruction?.instructions?.filters) {
        const hasDependency = this.hasFilterDependency(instruction.instructions.filters, completedContextKey);
        if (hasDependency) {
          dependentContexts.push(contextKey);
        }
      }
    });

    // Trigger each dependent context that now has its dependencies resolved
    dependentContexts.forEach(dependentContextKey => {
      if (this.areDependenciesResolved(dependentContextKey)) {
        this.refreshContext(dependentContextKey);
      }
    });
  }

  /**
   * Resolve `contextKey.fieldName` placeholders stored in contextInstructions filters
   * into concrete values.
   *
   * @param options.multiValue When false, an array-shaped context resolves to its first
   *        element only. Single-record instructions (limit 1) pass false, since matching
   *        several values against a limit-1 fetch would return an arbitrary row.
   */
  private resolveContextFilters(filters: any, options?: { multiValue?: boolean }): any {
    if (!filters || !this.viewManager) return filters;

    const resolvedFilters = { ...filters };
    const multiValue = options?.multiValue !== false;

    // Resolve a single entry, which may expand into several values when the
    // referenced context is an array (e.g. a Multi-Select selection).
    const resolveEntry = (value: any, matchMode?: string): any[] => {
      if (typeof value === 'string' && value.includes('.')) {
        // Check if this looks like a context reference (contextKey.fieldName)
        const [contextKey, fieldName] = value.split('.');
        return this.cellFilterService.resolveContextReferenceValues(
          this.viewManager.contextData,
          contextKey,
          fieldName,
          { multiValue, matchMode }
        );
      }
      return [value];
    };

    // Resolve context references in FilterCriteria format
    Object.keys(resolvedFilters).forEach(fieldId => {
      const filterCriteria = resolvedFilters[fieldId];

      // Handle new FilterCriteria format with values array
      if (filterCriteria && typeof filterCriteria === 'object' && filterCriteria.values && Array.isArray(filterCriteria.values)) {
        // Resolve each placeholder separately so a range filter keeps track of which
        // bound is which - a 'between' with only one bound resolved has to degrade to a
        // one-sided comparison rather than go out as an invalid one-value between.
        const resolvedPerPlaceholder = filterCriteria.values.map((value: any) =>
          resolveEntry(value, filterCriteria.matchMode));

        resolvedFilters[fieldId] = {
          ...filterCriteria,
          ...this.cellFilterService.buildResolvedCriteria(resolvedPerPlaceholder, filterCriteria.matchMode)
        };
      }
      // Handle old array format for backward compatibility
      else if (Array.isArray(filterCriteria)) {
        const resolvedValues: any[] = [];
        filterCriteria.forEach(value => {
          resolvedValues.push(...resolveEntry(value, 'exact'));
        });

        // Convert to new FilterCriteria format
        resolvedFilters[fieldId] = {
          values: resolvedValues.filter(v => v !== null && v !== undefined),
          matchMode: 'exact'
        };
      }
    });

    // Also handle dynamic filters format (for backward compatibility)
    if (resolvedFilters.dynamicFilters) {
      resolvedFilters.dynamicFilters = resolvedFilters.dynamicFilters.map((filter: any) => {
        if (filter.value && typeof filter.value === 'string' && filter.value.startsWith('{{') && filter.value.endsWith('}}')) {
          // Extract context reference
          const contextRef = filter.value.slice(2, -2); // Remove {{ }}
          const [contextKey, fieldName] = contextRef.split('.');

          if (this.viewManager.contextData[contextKey]) {
            const contextData = this.viewManager.contextData[contextKey];
            if (Array.isArray(contextData) && contextData.length > 0) {
              return { ...filter, value: contextData[0]?.[fieldName] ?? null };
            } else if (typeof contextData === 'object' && contextData !== null) {
              return { ...filter, value: contextData[fieldName] ?? null };
            }
          }

          // If context value not found, return filter with null value
          return { ...filter, value: null };
        }
        return filter;
      });
    }

    return resolvedFilters;
  }


  private refreshCellsUsingContext(contextKey: string): void {
    // Find and refresh only cells that consume this specific context
    this.cellInstances.forEach(cellInstance => {
      if (cellInstance.componentRef && this.cellConsumesContext(cellInstance, contextKey)) {
        const component = cellInstance.componentRef.instance;

        // Update viewManager first
        if (component.hasOwnProperty('viewManager')) {
          (component as any).viewManager = this.viewManager;
        }

        // Call reloadData to refresh the cell
        if (typeof component.reloadData === 'function') {
          component.reloadData();
        }

        // Trigger change detection
        cellInstance.componentRef.changeDetectorRef.detectChanges();
      }
    });

    // If a section's data source context changed, rebuild its rows entirely
    const needsSectionRebuild = this.sectionInstances.some(si =>
      si.section.dataSource === 'context' && si.section.contextVariable === contextKey
    );
    if (needsSectionRebuild) {
      // Re-setup cell instances to rebuild rows with new data, then re-render
      this.setupCellInstances();
      setTimeout(() => this.renderCells(), 50);
    }

    // Re-fetch dataset sections whose filters depend on this context
    this.sectionInstances.forEach(si => {
      if (si.section.dataSource === 'dataset' && si.section.datasetId && si.section.cellFilters) {
        const dependsOnContext = si.section.cellFilters.some(
          f => f.filterType === 'context' && f.contextObject === contextKey
        );
        if (dependsOnContext) {
          this.fetchSectionDatasetRows(si.section);
        }
      }
    });
  }

  private cellConsumesContext(cellInstance: CellInstance, contextKey: string): boolean {
    const cell = cellInstance.viewCell;

    // Generic check: if dataBinding.dataObjectKey matches, the cell likely consumes this context
    if (cell.dataBinding?.dataObjectKey === contextKey) {
      return true;
    }

    // Cell-type-specific checks for additional context consumption patterns
    switch (cell.type) {
      case CellType.TEXT:
        // Text cells in dynamic mode consume context
        return cell.configuration?.textMode === 'dynamic' &&
               cell.dataBinding?.dataObjectKey === contextKey;

      case CellType.FILTER_LIST:
        // Filter lists may consume context for filtering (check dataObjectKey already handled above)
        return false;

      case CellType.MULTI_SELECT:
        // Multi-select cells may consume context for filtering (check dataObjectKey already handled above)
        return false;

      case CellType.LIST_DISPLAY:
        // List display cells consume context for displaying the list
        return cell.configuration?.listDisplayContextVariable === contextKey;

      case CellType.DATA_DISPLAY:
        // Data display cells consume context (check dataObjectKey already handled above)
        return false;

      case CellType.INPUT:
        // Input cells with context data source consume context
        if (cell.configuration?.dataSource === 'context') {
          return cell.dataBinding?.dataObjectKey === contextKey;
        }
        // Lookup inputs may consume context for pre-filters
        if ((cell.configuration?.inputType as string) === 'lookup') {
          const cellFilters = cell.configuration?.lookupCellFilters || [];
          return cellFilters.some((filter: any) =>
            filter.filterType === 'context' && filter.contextObject === contextKey
          );
        }
        return false;

      case CellType.CHECKBOX:
        // Checkbox cells consume context if bound to it
        return cell.configuration?.dataSource === 'context' &&
               cell.dataBinding?.dataObjectKey === contextKey;

      case CellType.ACTION:
        // Action cells may consume context for their operations
        return false;

      case CellType.NOTE_BLOCK:
        // Note block cells consume context if bound to it
        return cell.configuration?.dataSource === 'context' &&
               cell.dataBinding?.dataObjectKey === contextKey;

      case CellType.RADIAL_BUTTON:
        // Radial button cells consume context if bound to it
        return cell.configuration?.dataSource === 'context' &&
               cell.dataBinding?.dataObjectKey === contextKey;

      case CellType.DATETIME_PICKER:
        // DateTime picker cells consume context if bound to it
        return cell.configuration?.dataSource === 'context' &&
               cell.dataBinding?.dataObjectKey === contextKey;

      case CellType.IMAGE:
        // Image cells with context source consume context
        return cell.configuration?.imageSource === 'context' &&
               cell.dataBinding?.contextVariable === contextKey;

      case CellType.PDF_TEXT:
        // PDF text cells consume context data
        return cell.dataBinding?.contextVariable === contextKey;

      case CellType.PDF_IMAGE:
        // PDF image cells with context source consume context
        return cell.configuration?.pdfImageSource === 'context' &&
               cell.dataBinding?.contextVariable === contextKey;

      case CellType.PDF_FIELD:
        // PDF field cells consume context data
        return cell.dataBinding?.contextVariable === contextKey;

      case CellType.PDF_TABLE:
        // PDF table cells consume context data
        return cell.dataBinding?.contextVariable === contextKey;

      case CellType.PDF_DATETIME:
        // PDF datetime cells are static (don't consume context)
        return false;

      case CellType.PDF_ROW:
        // PDF row cells consume context if any field element references the context
        const rowElements = cell.configuration?.pdfRowContainerElements || [];
        return rowElements.some((elem: any) =>
          elem.type === 'field' && elem.contextVariable === contextKey
        );

      case CellType.LOOKUP:
        // Lookup cells consume context if any of their context mappings reference this context
        // or if their pre-filters use this context
        const lookupMappings = cell.configuration?.lookupContextMappings || [];
        const lookupFilters = cell.configuration?.lookupCellFilters || [];
        return lookupMappings.some((mapping: any) => mapping.contextVariable === contextKey) ||
               lookupFilters.some((filter: any) =>
                 filter.filterType === 'context' && filter.contextObject === contextKey
               );

      case CellType.CALCULATION:
        // Calculation cells consume context if any operand references this context
        const calcOperands = cell.configuration?.calcOperands || [];
        if (calcOperands.some((op: any) => op.contextVariable === contextKey)) return true;
        // Also check conditional calculation dependencies
        if (cell.configuration?.calcConditionContextVariable === contextKey) return true;
        const calcConditions = cell.configuration?.calcConditions || [];
        return calcConditions.some((cond: any) =>
          cond.operands?.some((op: any) => op.contextVariable === contextKey)
        );

      case CellType.LIST_CALC:
        // List Calc cells consume context from the configured context variable
        // or from post-aggregation operands that reference context objects
        if (cell.configuration?.listCalcContextVariable === contextKey) return true;
        const listCalcPostOperands = cell.configuration?.listCalcPostOperands || [];
        return listCalcPostOperands.some((op: any) => op.contextVariable === contextKey);

      case CellType.PDF_LIST_CALC:
        // PDF List Calc cells consume context from the configured context variable
        // or from post-aggregation operands that reference context objects
        if (cell.configuration?.pdfListCalcContextVariable === contextKey) return true;
        const pdfPostOperands = cell.configuration?.pdfListCalcPostOperands || [];
        return pdfPostOperands.some((op: any) => op.contextVariable === contextKey);

      case CellType.DATA_ENTRY_TABLE:
        // In context-source mode the table both reads from and writes to its
        // bound context (configuration.contextVariable), so it does consume it.
        // In dataset mode it just emits its own rows; refresh is unnecessary.
        if (cell.configuration?.dataSource === 'context'
            && cell.configuration?.contextVariable === contextKey) {
          return true;
        }
        return false;

      case CellType.FILE_REFERENCE:
        return cell.dataBinding?.dataObjectKey === contextKey;

      case CellType.EMAIL_COMPOSE:
        // Rebuild the recipient list when its source array context changes
        // (e.g., the linked Multi-Select cell's selection is updated).
        return cell.configuration?.emailComposeContextVariable === contextKey;

      default:
        return false;
    }
  }

  private updateCellsWithNewContext(): void {
    // Update all cell instances with new context data (fallback method)
    this.cellInstances.forEach(cellInstance => {
      if (cellInstance.componentRef) {
        const component = cellInstance.componentRef.instance;
        if (component.hasOwnProperty('viewManager')) {
          (component as any).viewManager = this.viewManager;
          cellInstance.componentRef.changeDetectorRef.detectChanges();
        }
      }
    });
  }

  private onRefreshCells(cellIds: string[]): void {
    for (const cellId of cellIds) {
      const cellInstance = this.cellInstances.find(ci => ci.id === cellId);
      if (cellInstance?.componentRef) {
        const component = cellInstance.componentRef.instance;
        if (component.hasOwnProperty('viewManager')) {
          (component as any).viewManager = this.viewManager;
        }
        // Use reloadFromBackend if available (data-entry-table after save needs
        // fresh data with correct PKs), otherwise fall back to reloadData
        if (typeof (component as any).reloadFromBackend === 'function') {
          (component as any).reloadFromBackend();
        } else if (typeof component.reloadData === 'function') {
          component.reloadData();
        }
        cellInstance.componentRef.changeDetectorRef.detectChanges();
      }
    }
  }

  private navigateToTab(tabId: string): void {
    if (!this.currentViewDefinition) return;

    const targetTab = this.currentViewDefinition.tabs.find(tab => tab.id === tabId);
    if (targetTab && targetTab !== this.currentTab) {
      this.currentTab = targetTab;
      this.setupCellInstances();
      setTimeout(() => this.renderCells(), 100);
    }
  }

  onTabSelected(tab: ViewTab): void {
    if (tab !== this.currentTab) {
      // Exit PDF edit mode and clear overrides when switching tabs
      this.pdfEditMode = false;
      this.clearPdfOverrides();
      this.pdfEditingCellId = null;

      this.currentTab = tab;
      this.setupCellInstances();
      setTimeout(() => this.renderCells(), 100);
    }
  }

  getSortedTabs(): ViewTab[] {
    if (!this.currentViewDefinition) return [];
    return [...this.currentViewDefinition.tabs].sort((a, b) => a.order - b.order);
  }

  isPdfTab(): boolean {
    return this.currentTab?.tabType === 'pdf';
  }

  togglePdfEditMode(): void {
    this.pdfEditMode = !this.pdfEditMode;
    if (!this.pdfEditMode) {
      // Just stop editing, but keep overrides so edits persist for PDF generation
      this.pdfEditingCellId = null;
    }
    // Re-render cells so they pick up the new pdfEditMode flag
    if (this.currentTab) {
      this.renderCells();
      this.renderSectionCells();
    }
  }

  addPdfCell(type: 'text' | 'image'): void {
    if (!this.currentTab || !this.isPdfTab()) return;

    const cellId = `cell_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    if (type === 'text') {
      const newCell: ViewCell = {
        id: cellId,
        type: CellType.PDF_TEXT,
        position: { x: 20, y: 20, width: 300, height: 30 },
        configuration: {
          pdfValue: '',
          pdfFontSize: 12,
          pdfFontWeight: 'normal',
          pdfFontStyle: 'normal',
          pdfTextAlign: 'left',
          pdfColor: '#000000',
          _isPdfEditAddition: true
        },
        dataBinding: {}
      };
      this.currentTab.cells.push(newCell);
      // Append the single new instance instead of rebuilding the whole tab.
      // setupCellInstances() would tear down and re-derive every repeating
      // section, emptying their rows (dataset rows reset to a blank template
      // pending an async re-fetch; context-sourced rows get clobbered). Adding
      // a main cell must not touch the sections.
      this.cellInstances.push({ id: cellId, viewCell: newCell });
      setTimeout(() => {
        this.renderCells();
        // Auto-open the editor on the new cell
        setTimeout(() => {
          const newInstance = this.cellInstances.find(ci => ci.id === cellId);
          if (newInstance) {
            this.onPdfCellEditStart(newInstance);
          }
        }, 150);
      }, 100);
    } else if (type === 'image') {
      const newCell: ViewCell = {
        id: cellId,
        type: CellType.PDF_IMAGE,
        position: { x: 20, y: 20, width: 200, height: 150 },
        configuration: {
          pdfImageSource: 'static',
          pdfFilePath: '',
          _isPdfEditAddition: true
        },
        dataBinding: {}
      };
      this.currentTab.cells.push(newCell);
      // Append the single new instance instead of rebuilding the whole tab
      // (see the text branch above — setupCellInstances() empties the
      // repeating sections).
      this.cellInstances.push({ id: cellId, viewCell: newCell });
      setTimeout(() => {
        this.renderCells();
        // Auto-open the file browser for the new image cell
        setTimeout(() => {
          const newInstance = this.cellInstances.find(ci => ci.id === cellId);
          if (newInstance) {
            this.openFileBrowserForCell(newInstance);
          }
        }, 150);
      }, 100);
    }
  }

  onPdfCellEditStart(cellInstance: CellInstance): void {
    // Don't open editor if we just finished dragging
    if (this.pdfDragMoved) return;

    // For image cells, open the file browser instead of inline text editing
    if (cellInstance.viewCell.type === CellType.PDF_IMAGE) {
      this.openFileBrowserForCell(cellInstance);
      return;
    }

    // If this cell instance already has an override, use that text.
    // Otherwise capture the cell's current rendered text BEFORE showing the overlay.
    const existingOverride = this.pdfOverrides.get(cellInstance.id);
    if (existingOverride != null) {
      this.pdfEditingInitialText = existingOverride;
    } else if (cellInstance.componentRef) {
      this.pdfEditingInitialText = cellInstance.componentRef.location.nativeElement.textContent?.trim() || '';
    } else {
      this.pdfEditingInitialText = '';
    }

    this.pdfEditingCellId = cellInstance.id;

    // Wait for the contenteditable div to render, then populate and focus it
    setTimeout(() => {
      const editDiv = document.querySelector(`.pdf-edit-overlay[data-cell-id="${cellInstance.id}"]`) as HTMLElement;
      if (editDiv) {
        editDiv.innerText = this.pdfEditingInitialText;
        editDiv.focus();

        // Place cursor at end
        const range = document.createRange();
        range.selectNodeContents(editDiv);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    });
  }

  onPdfCellEditBlur(cellInstance: CellInstance, event: Event): void {
    const target = event.target as HTMLElement;
    const newText = target.innerText.trim();

    // Store override per cell instance ID so repeating section rows are independent
    this.pdfOverrides.set(cellInstance.id, newText);

    this.pdfEditingCellId = null;
  }

  hasPdfOverride(cellInstance: CellInstance): boolean {
    return this.pdfOverrides.has(cellInstance.id);
  }

  getPdfOverride(cellInstance: CellInstance): string {
    return this.pdfOverrides.get(cellInstance.id) || '';
  }

  onPdfCellEditCancel(): void {
    this.pdfEditingCellId = null;
  }

  clearPdfOverrides(): void {
    // Clean up any pdfOverrideValue that was written onto cell configs during PDF generation
    for (const cellInstance of this.cellInstances) {
      delete cellInstance.viewCell.configuration.pdfOverrideValue;
    }
    this.pdfOverrides.clear();
    this.pdfPositionOverrides.clear();
    this.pdfDeletedCellIds.clear();
  }

  openFileBrowserForCell(cellInstance: CellInstance): void {
    const dialogRef = this.dialog.open(FileBrowserModalComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: {
        title: 'Select Image',
        allowedStorageAreas: ['user', 'organization', 'admin']
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((filePath: string | null) => {
      if (filePath) {
        cellInstance.viewCell.configuration.pdfFilePath = filePath;
        cellInstance.viewCell.configuration.pdfImageSource = 'static';
        // Re-render so the image cell picks up the new file path
        if (cellInstance.componentRef) {
          cellInstance.componentRef.instance.cell = { ...cellInstance.viewCell };
          cellInstance.componentRef.instance.reloadData();
        }
      }
    });
  }

  onPdfCellDragStart(event: MouseEvent, cellInstance: CellInstance): void {
    // Only start drag on left-click, and don't interfere with editing
    if (event.button !== 0 || this.pdfEditingCellId === cellInstance.id) return;

    this.pdfDragStartX = event.clientX;
    this.pdfDragStartY = event.clientY;
    this.pdfDragOrigX = cellInstance.viewCell.position.x;
    // Main cells drag in visual (section-expanded) coordinates so they track the
    // cursor smoothly and can be placed over a repeating section. Section cells
    // keep their raw per-row Y (their drag is recorded as a per-row override).
    this.pdfDragOrigY = cellInstance.sectionId == null
      ? this.getCellTop(cellInstance)
      : cellInstance.viewCell.position.y;
    this.pdfDragCell = cellInstance;
    this.pdfDragMoved = false;

    this.boundOnPdfDragMove = (e: MouseEvent) => this.onPdfDragMove(e);
    this.boundOnPdfDragEnd = (e: MouseEvent) => this.onPdfDragEnd(e);
    document.addEventListener('mousemove', this.boundOnPdfDragMove);
    document.addEventListener('mouseup', this.boundOnPdfDragEnd);
  }

  private onPdfDragMove(event: MouseEvent): void {
    if (!this.pdfDragCell) return;

    const dx = event.clientX - this.pdfDragStartX;
    const dy = event.clientY - this.pdfDragStartY;

    // Only start visual drag after a small threshold to distinguish from clicks
    if (!this.pdfDragMoved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
    this.pdfDragMoved = true;
    this.pdfDraggingCellId = this.pdfDragCell.id;

    this.pdfDragCell.viewCell.position.x = Math.max(0, this.pdfDragOrigX + dx);
    this.pdfDragCell.viewCell.position.y = Math.max(0, this.pdfDragOrigY + dy);
    // Once dragged, a main cell stores its Y in absolute visual coordinates
    // (already including any section expansion), so getCellTop renders it as-is
    // instead of re-applying the offset — this is what lets it sit over a section.
    if (this.pdfDragCell.sectionId == null) {
      this.pdfDragCell.viewCell.configuration._pdfAbsoluteY = true;
    }
  }

  private onPdfDragEnd(event: MouseEvent): void {
    if (this.boundOnPdfDragMove) {
      document.removeEventListener('mousemove', this.boundOnPdfDragMove);
    }
    if (this.boundOnPdfDragEnd) {
      document.removeEventListener('mouseup', this.boundOnPdfDragEnd);
    }

    // If user dragged, suppress the click that would open the editor
    if (this.pdfDragMoved && this.pdfDragCell) {
      // Record per-row position for section cells so the edit is isolated to
      // this row and carried into PDF generation.
      this.recordSectionPositionOverride(this.pdfDragCell);
      // Prevent the click event from firing by briefly removing the click target
      const cellId = this.pdfDragCell.id;
      setTimeout(() => {
        if (this.pdfDraggingCellId === cellId) {
          this.pdfDraggingCellId = null;
        }
      });
    }

    this.pdfDragCell = null;
    this.boundOnPdfDragMove = null;
    this.boundOnPdfDragEnd = null;
  }

  onPdfCellResizeStart(event: MouseEvent, cellInstance: CellInstance): void {
    event.preventDefault();
    event.stopPropagation();

    this.pdfResizeStartX = event.clientX;
    this.pdfResizeStartY = event.clientY;
    this.pdfResizeOrigW = cellInstance.viewCell.position.width;
    this.pdfResizeOrigH = cellInstance.viewCell.position.height;
    this.pdfResizeCell = cellInstance;
    this.pdfResizingCellId = cellInstance.id;

    this.boundOnPdfResizeMove = (e: MouseEvent) => this.onPdfResizeMove(e);
    this.boundOnPdfResizeEnd = (e: MouseEvent) => this.onPdfResizeEnd(e);
    document.addEventListener('mousemove', this.boundOnPdfResizeMove);
    document.addEventListener('mouseup', this.boundOnPdfResizeEnd);
  }

  private onPdfResizeMove(event: MouseEvent): void {
    if (!this.pdfResizeCell) return;

    const dx = event.clientX - this.pdfResizeStartX;
    const dy = event.clientY - this.pdfResizeStartY;

    this.pdfResizeCell.viewCell.position.width = Math.max(20, this.pdfResizeOrigW + dx);
    this.pdfResizeCell.viewCell.position.height = Math.max(10, this.pdfResizeOrigH + dy);
  }

  private onPdfResizeEnd(event: MouseEvent): void {
    if (this.boundOnPdfResizeMove) {
      document.removeEventListener('mousemove', this.boundOnPdfResizeMove);
    }
    if (this.boundOnPdfResizeEnd) {
      document.removeEventListener('mouseup', this.boundOnPdfResizeEnd);
    }

    // Record per-row size for section cells so the edit is isolated to this row.
    this.recordSectionPositionOverride(this.pdfResizeCell);

    this.pdfResizingCellId = null;
    this.pdfResizeCell = null;
    this.boundOnPdfResizeMove = null;
    this.boundOnPdfResizeEnd = null;
  }

  /**
   * If the given instance is a repeating-section cell, snapshot its current
   * position/size into the ephemeral per-row override map. Main (non-section)
   * cells are skipped — they mutate their own viewCell.position directly, which
   * is already what PDF generation reads.
   */
  /**
   * Delete a cell in PDF edit mode. The deletion is ephemeral (kept in
   * pdfDeletedCellIds) so it survives re-renders, feeds into PDF generation, and
   * is reset on tab switch / reload. For repeating-section cells the instance ID
   * is row-specific, so only that one row's cell is removed.
   */
  deletePdfCell(event: Event, cellInstance: CellInstance): void {
    event.stopPropagation();
    if (!this.pdfEditMode) return;

    this.pdfDeletedCellIds.add(cellInstance.id);

    // Drop any edits tied to this instance
    this.pdfOverrides.delete(cellInstance.id);
    this.pdfPositionOverrides.delete(cellInstance.id);
    if (this.pdfEditingCellId === cellInstance.id) {
      this.pdfEditingCellId = null;
    }

    // Tear down the rendered component
    if (cellInstance.componentRef) {
      cellInstance.componentRef.destroy();
      cellInstance.componentRef = undefined;
    }

    // Remove from the rendered collection so it disappears immediately
    if (cellInstance.sectionId != null) {
      const section = this.sectionInstances.find(si => si.id === cellInstance.sectionId);
      section?.rows.forEach(row => {
        row.cellInstances = row.cellInstances.filter(ci => ci.id !== cellInstance.id);
      });
    } else {
      this.cellInstances = this.cellInstances.filter(ci => ci.id !== cellInstance.id);
    }
  }

  private recordSectionPositionOverride(cellInstance: CellInstance | null): void {
    if (!cellInstance || cellInstance.sectionId == null) return;
    const pos = cellInstance.viewCell.position;
    this.pdfPositionOverrides.set(cellInstance.id, {
      x: pos.x,
      y: pos.y,
      width: pos.width,
      height: pos.height,
      zIndex: pos.zIndex
    });
  }

  /**
   * Calculate the canvas height accounting for section expansion.
   */
  getAdjustedCanvasHeight(): number {
    const baseHeight = this.currentTab?.canvas.height || 800;
    return baseHeight + this.getSectionExpansion();
  }

  /**
   * Total extra height from all expanded sections.
   */
  private getSectionExpansion(): number {
    let expansion = 0;
    this.sectionInstances.forEach(si => {
      const extraRows = Math.max(si.rows.length - 1, 0);
      expansion += extraRows * si.rowHeight;
    });
    return expansion;
  }

  /**
   * Get the adjusted Y position for a regular cell, accounting for
   * section expansions above it.
   */
  getAdjustedCellY(cellY: number): number {
    let offset = 0;
    this.sectionInstances.forEach(si => {
      // If the cell is below this section, push it down by the section's expansion
      if (cellY > si.section.y) {
        const extraRows = Math.max(si.rows.length - 1, 0);
        offset += extraRows * si.rowHeight;
      }
    });
    return cellY + offset;
  }

  /**
   * Visual top (px) for a main canvas cell. Cells dragged over a repeating
   * section in PDF edit mode store their Y in absolute, already-expanded
   * coordinates (flagged with _pdfAbsoluteY) and render at that Y directly;
   * all other cells get the section-expansion offset applied so they stay below
   * an expanded section as before.
   */
  getCellTop(cellInstance: CellInstance): number {
    if (cellInstance.viewCell.configuration?._pdfAbsoluteY) {
      return cellInstance.viewCell.position.y;
    }
    return this.getAdjustedCellY(cellInstance.viewCell.position.y);
  }

  /**
   * Check if a tab is enabled based on its required contexts being loaded
   */
  isTabEnabled(tab: ViewTab): boolean {
    // If no required contexts, tab is always enabled
    if (!tab.requiredContexts || tab.requiredContexts.length === 0) {
      return true;
    }

    // Check if all required contexts have been loaded with data
    if (!this.viewManager?.contextData) {
      return false;
    }

    return tab.requiredContexts.every(contextKey => {
      const contextValue = this.viewManager!.contextData[contextKey];
      // Context is considered loaded if it exists and is not null/undefined
      return contextValue !== null && contextValue !== undefined;
    });
  }

  /**
   * Get tooltip text for a disabled tab explaining what contexts are missing
   */
  getTabDisabledReason(tab: ViewTab): string {
    if (!tab.requiredContexts || tab.requiredContexts.length === 0) {
      return '';
    }

    const missingContexts = tab.requiredContexts.filter(contextKey => {
      const contextValue = this.viewManager?.contextData?.[contextKey];
      return contextValue === null || contextValue === undefined;
    });

    if (missingContexts.length === 0) {
      return '';
    }

    return `Requires: ${missingContexts.join(', ')}`;
  }

  generatePdf(): void {
    if (!this.selectedView || !this.currentTab || !this.isPdfTab()) {
      return;
    }

    this.generatingPdf = true;

    // Get the current context data
    const contextData = this.viewManager?.contextData || {};

    // Apply overrides from the Map onto cell configs for PDF generation.
    // Main cells have unique configs so this is safe.
    for (const cellInstance of this.cellInstances) {
      const override = this.pdfOverrides.get(cellInstance.id);
      if (override != null) {
        cellInstance.viewCell.configuration.pdfOverrideValue = override;
      }
    }

    this.pdfGenerationService.generatePdf(
      this.selectedView.id,
      this.currentTab,
      contextData,
      this.viewManager,
      this.pdfOverrides,
      this.pdfPositionOverrides,
      this.pdfDeletedCellIds
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (blob) => {
        this.generatingPdf = false;

        // Generate filename with view name and timestamp
        const timestamp = new Date().toISOString().slice(0, 10);
        const filename = `${this.selectedView!.name}_${this.currentTab!.name}_${timestamp}.pdf`;

        this.pdfGenerationService.downloadPdf(blob, filename);
      },
      error: (error) => {
        this.generatingPdf = false;
        console.error('Error generating PDF:', error);
        alert('Failed to generate PDF. Please try again.');
      }
    });
  }

  refreshView(): void {
    if (this.selectedView) {
      this.loadViewData();
    }
  }

  private cleanupCurrentView(): void {
    this.cleanupCells();
    this.currentViewDefinition = null;
    this.viewManager = null;
    this.currentTab = null;
    this.cellInstances = [];
    this.lastSectionFilterSignatures = {};
    this.sectionFetchesInFlight.clear();
  }

  trackByCellId(index: number, cellInstance: CellInstance): string {
    return cellInstance.id;
  }

  trackBySectionId(index: number, sectionInstance: SectionInstance): string {
    return sectionInstance.id;
  }

  private cleanupCells(): void {
    this.cellInstances.forEach(cellInstance => {
      if (cellInstance.componentRef) {
        cellInstance.componentRef.destroy();
      }
    });
    this.cellInstances = [];

    // Clean up section cells
    this.sectionInstances.forEach(section => {
      section.rows.forEach(row => {
        row.cellInstances.forEach(cellInstance => {
          if (cellInstance.componentRef) {
            cellInstance.componentRef.destroy();
          }
        });
      });
    });
    this.sectionInstances = [];
  }

  private hasFilterDependency(filters: any, contextKey: string): boolean {
    if (!filters) return false;

    // Check each filter field for context references
    for (const fieldId in filters) {
      const fieldValue = filters[fieldId];
      const valuesToCheck = this.extractFilterValues(fieldValue);

      for (const value of valuesToCheck) {
        if (typeof value === 'string' && value.includes('.')) {
          const [refContextKey] = value.split('.');
          if (refContextKey === contextKey) {
            return true;
          }
        }
      }
    }

    return false;
  }

  private getAllContextDependencies(instruction: any): string[] {
    const dependencies: Set<string> = new Set();

    // Add explicit dependency
    if (instruction?.depContextObj) {
      dependencies.add(instruction.depContextObj);
    }

    // Add filter-based dependencies
    if (instruction?.instructions?.filters) {
      for (const fieldId in instruction.instructions.filters) {
        const fieldValue = instruction.instructions.filters[fieldId];
        const valuesToCheck = this.extractFilterValues(fieldValue);

        for (const value of valuesToCheck) {
          if (typeof value === 'string' && value.includes('.')) {
            const [contextKey] = value.split('.');
            // Only add if it looks like a context reference (not a regular value with a dot)
            if (contextKey && this.viewManager?.contextData?.hasOwnProperty(contextKey)) {
              dependencies.add(contextKey);
            }
          }
        }
      }
    }

    return Array.from(dependencies);
  }

  /**
   * Extracts the values array from a filter entry, handling both old array format
   * and new FilterCriteria format ({ values: [...], matchMode: '...' }).
   */
  private extractFilterValues(filterEntry: any): any[] {
    if (Array.isArray(filterEntry)) {
      return filterEntry;
    }
    if (filterEntry && typeof filterEntry === 'object' && Array.isArray(filterEntry.values)) {
      return filterEntry.values;
    }
    return [];
  }
}
